import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { BRAND, batchLabel, errorMessage, handleError, toast, useBodyScrollLock } from "./shared";

// Pratinjau + konfirmasi menerapkan konfigurasi batch ke student yang sudah
// ada. Hanya MENAMBAH barang yang belum dimiliki student; tidak menghapus
// dan tidak mengubah status barang yang sudah ada.
const ApplyPreviewModal = ({ batch, onClose, onApplied }) => {
  useBodyScrollLock();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [chosen, setChosen] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setPlan(null);
    setError("");
    try {
      const res = await axios.get(`/student-package/batches/${batch}/apply-preview`);
      setPlan(res.data);
      setChosen(res.data.items.map((i) => i.packageItemId));
    } catch (err) {
      if (err?.response?.status === 401) return handleError(err, navigate);
      setError(errorMessage(err, "Unable to load the preview."));
    }
  }, [batch, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Angka mengikuti barang yang dicentang — sama dengan yang dihitung ulang
  // server saat konfirmasi.
  const summary = useMemo(() => {
    if (!plan) return { records: 0, students: [] };
    const students = plan.students
      .map((s) => ({ ...s, items: s.items.filter((i) => chosen.includes(i.packageItemId)) }))
      .filter((s) => s.items.length > 0);
    return { records: students.reduce((n, s) => n + s.items.length, 0), students };
  }, [plan, chosen]);

  const confirm = async () => {
    const result = await Swal.fire({
      title: "Apply to existing students?",
      html: `<b>${summary.records}</b> pending record(s) will be created for <b>${summary.students.length}</b> student(s) in ${batchLabel(batch)}.<br/><span style="font-size:13px;color:#6b7280">Nothing is deleted and no received item is changed.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, create records",
      cancelButtonText: "Cancel",
      confirmButtonColor: BRAND,
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setSaving(true);
    try {
      const res = await axios.post(`/student-package/batches/${batch}/apply`, { itemIds: chosen, expectedRecords: summary.records });
      toast(res.data.msg);
      onApplied && onApplied();
      onClose();
    } catch (err) {
      if (err?.response?.data?.code === "PREVIEW_OUTDATED") {
        Swal.fire({ text: err.response.data.msg, icon: "info", confirmButtonColor: BRAND });
        load();
      } else {
        handleError(err, navigate, "Unable to apply the configuration.");
      }
    } finally {
      setSaving(false);
    }
  };

  const visibleStudents = showAll ? summary.students : summary.students.slice(0, 30);

  return createPortal(
    <div className="adm-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className="adm-modal spk-detail" role="dialog" aria-modal="true" aria-labelledby="spk-apply-title" onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal-header">
          <div>
            <h5 id="spk-apply-title" className="adm-card-title" style={{ fontSize: "17px" }}>
              Apply {batchLabel(batch)} items to existing students
            </h5>
            <p className="adm-card-subtitle">
              Adds the items each student does not have yet, as pending. Existing records and received items are never changed.
            </p>
          </div>
          <button type="button" className="adm-modal-close" aria-label="Close" onClick={onClose} disabled={saving}>
            ×
          </button>
        </div>
        <div className="adm-modal-body">
          {!plan && !error && <p className="adm-muted-text">Loading preview…</p>}
          {error && (
            <div className="spk-callout is-error" role="alert">
              <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
              <div>
                <p className="spk-callout-text">{error}</p>
                <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={load}>
                  Try again
                </button>
              </div>
            </div>
          )}
          {plan && plan.totalRecords === 0 && (
            <div className="spk-callout">
              <i className="bi bi-check2-circle" aria-hidden="true"></i>
              <div>
                <p className="spk-callout-title">Nothing to add</p>
                <p className="spk-callout-text">Every student in this batch already has all of the batch's active items.</p>
              </div>
            </div>
          )}
          {plan && plan.totalRecords > 0 && (
            <>
              <p className="adm-form-section-title" style={{ marginTop: 0 }}>Items to add</p>
              <div className="spk-apply-items">
                {plan.items.map((item) => (
                  <label key={item.packageItemId} className="spk-apply-item">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={chosen.includes(item.packageItemId)}
                      onChange={() =>
                        setChosen((prev) =>
                          prev.includes(item.packageItemId) ? prev.filter((x) => x !== item.packageItemId) : [...prev, item.packageItemId],
                        )
                      }
                    />
                    <span>{item.itemName}</span>
                    <span className="spk-dim">{item.students} student(s)</span>
                  </label>
                ))}
              </div>

              <p className="adm-form-section-title">
                Affected students ({summary.students.length}) · {summary.records} record(s)
              </p>
              {summary.students.length === 0 ? (
                <p className="adm-muted-text">Choose at least one item.</p>
              ) : (
                <div className="adm-table-wrap spk-apply-table">
                  <table className="adm-table adm-table--compact">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Items to add</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleStudents.map((s) => (
                        <tr key={s.studentRowId}>
                          <td>
                            <div className="adm-cell-title">{s.name}</div>
                            <div className="adm-cell-meta">
                              {s.studentId}
                              {!s.hasRecords && <span className="spk-tag">first package records</span>}
                            </div>
                          </td>
                          <td>{s.items.map((i) => i.itemName).join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {summary.students.length > 30 && !showAll && (
                <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm mt-2" onClick={() => setShowAll(true)}>
                  Show all {summary.students.length} students
                </button>
              )}
            </>
          )}
        </div>
        <div className="adm-modal-footer">
          <button type="button" className="adm-btn adm-btn--secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {plan && plan.totalRecords > 0 && (
            <button type="button" className="adm-btn adm-btn--primary" onClick={confirm} disabled={saving || summary.records === 0}>
              {saving ? "Applying…" : `Create ${summary.records} record(s)`}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ApplyPreviewModal;
